import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { usePermission } from '@fs/zion-permissions'

export const ImageOrganizeContext = React.createContext({})

export default function ImageOrganizeProvider({ children, parentGroup }) {
  /* dividerOffsets is a list of indexes. Each index corresponds to the start of a child group in the parent's list of image APIDs. */
  const [dividerOffsets, setDividerOffsets] = useState([])
  const [initialDividers, setInitialDividers] = useState(null)
  const [skippedItemsMap, setSkippedItemsMap] = useState({}) // Keeps tracked of skipped items during "Correct Metadata"
  const [hasImageOrganizePermissions, imageOrganizePermissionStatus] = usePermission(
    'LightyearImageOrganizePermission',
    'LightyearContext'
  )
  const [rmsGroupWritePermission] = usePermission('RMSGroupWritePermission', 'RMSContext')
  const [rmsAdminPermissions] = usePermission('RMSAdminPermission', 'RMSContext')
  const hasEditPermissions = hasImageOrganizePermissions && (rmsGroupWritePermission || rmsAdminPermissions)

  const removeDivider = useCallback(
    (offsetToRemove) => {
      if (dividerOffsets?.length < 1) setDividerOffsets([])
      else setDividerOffsets(dividerOffsets.reduce((acc, i) => (i !== offsetToRemove ? [...acc, i] : acc), []))
    },
    [dividerOffsets]
  )

  const addNewDivider = useCallback(
    (endImageApid) => {
      const apidList = parentGroup?.siblingIdsWithChildren
        ? parentGroup.siblingIdsWithChildren.flatMap((child) => child.childIds)
        : parentGroup.imageApids

      setDividerOffsets((previousOffsets) => {
        const index = apidList?.findIndex((el) => el === endImageApid)
        if (index === apidList.length) return previousOffsets

        const asSet = new Set([...previousOffsets, index + 1])
        return Array.from(asSet).sort((a, b) => a - b)
      })
    },
    [parentGroup]
  )

  useEffect(() => {
    if (parentGroup && initialDividers === null) {
      const newOffsets = getDividerOffsets(parentGroup)
      setDividerOffsets(newOffsets)
      if (initialDividers === null) setInitialDividers(newOffsets)
    }
  }, [parentGroup, initialDividers])

  const dividersAltered = useMemo(() => {
    if (initialDividers?.length !== dividerOffsets?.length) return true
    for (let i = 0; i < dividerOffsets.length; i++) if (initialDividers[i] !== dividerOffsets[i]) return true

    return false
  }, [dividerOffsets, initialDividers])

  const state = useMemo(
    () => ({
      dividersAltered,
      dividerOffsets,
      addNewDivider,
      removeDivider,
      skippedItemsMap,
      setSkippedItemsMap,
      hasImageOrganizePermissions,
      imageOrganizePermissionsLoaded: imageOrganizePermissionStatus === 'loaded',
      hasEditPermissions,
    }),
    [
      dividersAltered,
      dividerOffsets,
      addNewDivider,
      removeDivider,
      skippedItemsMap,
      setSkippedItemsMap,
      hasImageOrganizePermissions,
      imageOrganizePermissionStatus,
      hasEditPermissions,
    ]
  )

  return <ImageOrganizeContext.Provider value={state}>{children}</ImageOrganizeContext.Provider>
}

function getDividerOffsets(group) {
  const property = group?.siblingIdsWithChildren ? 'siblingIdsWithChildren' : 'childGroups'
  const imagePropertyName = group?.siblingIdsWithChildren ? 'childIds' : 'imageApids'
  const subGroups = group[property]

  if (!Array.isArray(subGroups) || subGroups?.length === 0) return [0]

  const offsets = []
  let offset = 0
  subGroups.forEach((g) => {
    offsets.push(offset)
    offset += g[imagePropertyName]?.length ?? 0
  })

  return offsets
}
